import { NavLink } from "react-router-dom";
import {
    SquaresFour,
    Link as LinkIcon,
    Waveform,
    Brain,
    Lightbulb,
    PenNib,
    TrendUp,
    PlugsConnected,
    GearSix,
    Broadcast,
    Flask,
    Info,
    Newspaper,
    FlowArrow,
    Users,
    Wallet,
    FilmStrip,
    SignOut,
    ShieldCheck,
    User as UserIcon,
} from "@phosphor-icons/react";
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "./ui/tooltip";
import ThemeToggle from "./ThemeToggle";
import HowThisWorks from "./HowThisWorks";
import { useTheme } from "../lib/theme";
import { useAuth } from "../lib/auth";

// `roles` defaults to ["admin","user"] (visible to all). Items with
// roles=["admin"] are only shown to admins.
const NAV = [
    { to: "/", label: "Briefing", icon: SquaresFour, end: true, testid: "nav-dashboard",
      roles: ["admin", "user"],
      tooltip: "Your live intelligence report. See the strongest emotional patterns, tensions, language shifts, and cultural observations emerging across modern dating conversations." },
    { to: "/workflow", label: "Workflow", icon: FlowArrow, testid: "nav-workflow",
      roles: ["admin"],
      tooltip: "The intelligence pipeline. See where every stream sits in the journey from raw signal to published content, and what the single most useful next action is for each." },
    { to: "/weekly-brief", label: "Weekly Brief", icon: Newspaper, testid: "nav-weekly-brief",
      roles: ["admin", "user"],
      tooltip: "The long-form weekly intelligence brief. A printable, editorially structured synthesis of cultural shifts, signal metrics, tensions, vocabulary shifts, behavioral patterns, strategic interpretation, and forecasts." },
    { to: "/streams", label: "Streams", icon: Broadcast, testid: "nav-streams",
      roles: ["admin", "user"],
      adminTooltip: "Define what conversations the system should continuously monitor. Create listening strategies using keywords, themes, hashtags, subreddits, creators, or URLs.",
      tooltip: "See which cultural conversations HAEVN Labs is continuously listening to — the topics, hashtags, subreddits, and creators each stream is tracking across platforms." },
    { to: "/sources", label: "Manual sources", icon: LinkIcon, testid: "nav-sources",
      roles: ["admin"],
      tooltip: "The actual posts, videos, threads, and conversations collected by your Streams. These are the origins of your signals." },
    { to: "/signals", label: "Raw Signals", icon: Waveform, testid: "nav-signals",
      roles: ["admin"],
      tooltip: "The real comments and language people are using. Review emotional reactions, repeated phrases, frustrations, and behavioral patterns directly from the source." },
    { to: "/analyses", label: "Analyses", icon: Brain, testid: "nav-analyses",
      roles: ["admin", "user"],
      tooltip: "AI-powered interpretation of collected signals. Surface recurring emotional themes, contradictions, cultural tensions, and emerging narratives." },
    { to: "/insights", label: "Insights", icon: Lightbulb, testid: "nav-insights",
      roles: ["admin", "user"],
      tooltip: "Save the strongest observations worth remembering. This is the long-term archive of emotional truths and cultural intelligence." },
    { to: "/content-ideas", label: "Content Ideas", icon: PenNib, testid: "nav-content-ideas",
      roles: ["admin", "user"],
      tooltip: "Turn insights into founder talking points, carousels, videos, podcasts, PR angles, and narrative-driven content concepts." },
    { to: "/labs", label: "Narrative Labs", icon: Flask, testid: "nav-labs",
      roles: ["admin", "user"],
      tooltip: "Pressure-test messaging ideas, emotional hypotheses, and positioning concepts against real-world signals." },
    { to: "/trends", label: "Trends", icon: TrendUp, testid: "nav-trends",
      roles: ["admin", "user"],
      tooltip: "Track how narratives, emotional themes, and language evolve over time across platforms and conversations." },
    { to: "/integrations", label: "Integrations", icon: PlugsConnected, testid: "nav-integrations",
      roles: ["admin"],
      tooltip: "Connect and manage the systems powering signal collection and AI analysis." },
    { to: "/admin/users", label: "Users", icon: Users, testid: "nav-admin-users",
      roles: ["admin"],
      tooltip: "Create, manage, and revoke access to HAEVN Labs. Admin-only." },
    { to: "/budget", label: "Budget", icon: Wallet, testid: "nav-budget",
      roles: ["admin"],
      tooltip: "Private marketing-spend tracker. Drop a receipt or invoice in and the AI extracts vendor, amount, date, and a suggested category — you confirm to log it against the planned budget." },
    { to: "/content-studio", label: "Content Studio", icon: FilmStrip, testid: "nav-content-studio",
      roles: ["admin"],
      tooltip: "HAEVN's internal content operating system. Move pieces from idea through approval and scheduling to publication — with media uploads, parent/child relationships, and Buffer-ready settings." },
    { to: "/settings", label: "Settings", icon: GearSix, testid: "nav-settings",
      roles: ["admin"],
      tooltip: "Configure system behavior, prompts, defaults, tags, AI preferences, and platform settings." },
];

export default function Sidebar() {
    const { theme } = useTheme();
    const { user, logout, isAdmin } = useAuth();
    const logoSrc = theme === "dark" ? "/haevn-logo-alt.png" : "/haevn-mark.png";
    const visibleNav = NAV.filter((item) => !item.roles || item.roles.includes(user?.role || "user"));

    return (
        <aside
            data-testid="app-sidebar"
            className="w-[248px] shrink-0 border-r border-border bg-sidebar-bg text-white/85 flex flex-col h-screen sticky top-0"
        >
            <div className="px-6 pt-8 pb-5">
                <NavLink to="/" data-testid="brand-link" className="block">
                    <img
                        src={logoSrc}
                        alt="HAEVN"
                        data-testid="brand-logo"
                        className="h-5 w-auto select-none invert brightness-0"
                        draggable={false}
                    />
                    <div className="overline text-[9px] mt-2.5 text-white/55">
                        Intelligence · HAEVN Labs
                    </div>
                </NavLink>
            </div>

            <div className="px-3 pb-3">
                <HowThisWorks />
            </div>

            <TooltipProvider delayDuration={200}>
                <nav className="px-3 flex-1 overflow-y-auto">
                    <div className="overline px-3 pb-2 text-white/55">Navigate</div>
                    <ul className="space-y-0.5">
                        {visibleNav.map((item) => {
                            const Icon = item.icon;
                            return (
                                <li key={item.to} className="group/row relative">
                                    <NavLink
                                        to={item.to}
                                        end={item.end}
                                        data-testid={item.testid}
                                        className={({ isActive }) =>
                                            [
                                                "flex items-center gap-2.5 pl-3 pr-2 py-2 rounded-sm text-sm border-l-2 transition-colors duration-150",
                                                isActive
                                                    ? "border-accent bg-white/10 text-white font-semibold"
                                                    : "border-transparent text-white/70 hover:text-white hover:bg-white/5",
                                            ].join(" ")
                                        }
                                    >
                                        <Icon size={16} weight="duotone" />
                                        <span className="flex-1">{item.label}</span>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    type="button"
                                                    data-testid={`${item.testid}-info`}
                                                    onClick={(e) => { e.preventDefault(); }}
                                                    className="opacity-40 hover:opacity-100 hover:text-accent p-0.5 rounded-sm text-white"
                                                    aria-label={`About ${item.label}`}
                                                >
                                                    <Info size={11} weight="duotone" />
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent
                                                side="right"
                                                align="start"
                                                sideOffset={10}
                                                className="max-w-[260px] text-[12px] leading-relaxed editorial bg-popover border border-border text-popover-foreground p-3 rounded-sm"
                                            >
                                                <div className="font-display font-semibold text-[13px] mb-1 not-italic text-foreground">{item.label}</div>
                                                {(isAdmin && item.adminTooltip) ? item.adminTooltip : item.tooltip}
                                            </TooltipContent>
                                        </Tooltip>
                                    </NavLink>
                                </li>
                            );
                        })}
                    </ul>
                </nav>
            </TooltipProvider>

            {/* User identity + sign out */}
            {user && (
                <div className="px-3 py-3 border-t border-white/10" data-testid="sidebar-user-card">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-sm bg-white/5">
                        {user.role === "admin"
                            ? <ShieldCheck size={14} weight="duotone" className="text-accent shrink-0" />
                            : <UserIcon size={14} weight="duotone" className="text-white/60 shrink-0" />
                        }
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-semibold text-[12px] text-white leading-tight truncate" data-testid="sidebar-user-name">
                                {user.name || user.email.split("@")[0]}
                            </div>
                            <div className="font-mono text-[9px] tracking-wider text-white/50 uppercase">
                                {user.role}
                            </div>
                        </div>
                        <button
                            type="button"
                            data-testid="sidebar-logout-btn"
                            onClick={logout}
                            className="text-white/50 hover:text-accent p-1"
                            title="Sign out"
                            aria-label="Sign out"
                        >
                            <SignOut size={13} weight="bold" />
                        </button>
                    </div>
                </div>
            )}

            <div className="p-3 border-t border-white/10">
                <ThemeToggle />
            </div>
        </aside>
    );
}
